// React-Portal zum Rendern des Modals außerhalb der normalen DOM-Hierarchie
import { createPortal } from "react-dom";

// useRef für Zugriff auf DOM-Elemente (z. B. zum Drucken)
import { useRef } from "react";

// Eigener Context zur Sprachverwaltung
import { useLanguage } from "./LanguageContext";

/**
 * StandModal
 * Modal-Komponente zur Anzeige detaillierter Informationen
 * eines Messestandes inkl. Teilen- und Druckfunktion.
 */
export default function StandModal({ stand, onClose, pdfMode = false }) {
  // Referenz auf den druckbaren Bereich des Modals
  const printRef = useRef(null);

  // Aktuelle Sprache aus dem LanguageContext
  const { lang } = useLanguage();

  // Kiosk-Modus prüfen
  const isKiosk =
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.userAgent.includes("Kiosk");

  /**
   * Übersetzungs-Hilfsfunktion
   * - akzeptiert String oder Sprachobjekt
   * - fallback ist Deutsch
   */
  const t = (field, fallback = "de") => {
    if (!field) return "";
    if (typeof field === "string") return field;
    return field[lang] ?? field[fallback] ?? "";
  };

  // Wenn kein Stand übergeben wird, nichts rendern
  if (!stand) return null;

  /**
   * Share-Funktion
   * - Mobile: Web Share API
   * - Desktop: URL in Zwischenablage kopieren
   */
  const handleShare = () => {
    const shareData = {
      title: t(stand.company),
      text: `${
        lang === "de" ? "Schau dir den Stand von" : "Check out the stand of"
      } ${t(stand.company)}!`,
      url: window.location.href + `#stand-${stand.id}`,
    };

    if (navigator.share) {
      navigator
        .share(shareData)
        .then(() => console.log("Erfolgreich geteilt"))
        .catch((err) => console.error("Teilen fehlgeschlagen", err));
    } else {
      navigator.clipboard
        .writeText(shareData.url)
        .then(() =>
          alert(lang === "de" ? "Link kopiert!" : "Link copied!")
        )
        .catch(() =>
          alert(
            lang === "de"
              ? "Link konnte nicht kopiert werden"
              : "Link could not be copied"
          )
        );
    }
  };

  /**
   * Druck-/Speicher-Funktion
   * Öffnet ein neues Fenster mit dem Modal-Inhalt
   * und startet den Druckdialog
   */
  const handlePrint = () => {
    if (!printRef.current) return;

    const newWindow = window.open("", "printWindow", "width=900,height=1200"); // breiter für PDF
    if (!newWindow) return;

    const content = printRef.current.cloneNode(true);

    // Füge Klasse hinzu, damit PDF-Mode greift
    content.querySelectorAll("*").forEach(el => el.classList.add("pdf-mode"));

    newWindow.document.open();
    newWindow.document.write(`
      <html>
        <head>
          <title>${t(stand.company)}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            img.pdf-mode { width: 100%; max-width: 600px; height: auto; display:block; margin:auto; }
            h2.pdf-mode { font-size: 24px; }
            p.pdf-mode { font-size: 20px; }
            button { display: none; }
          </style>
        </head>
        <body></body>
      </html>
    `);
    newWindow.document.close();

    newWindow.document.body.appendChild(content);

    setTimeout(() => {
      newWindow.print();
      newWindow.close();
    }, 300);
  };


  /**
   * Modal-Rendering via Portal
   */
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div
          ref={printRef}
          className="relative bg-white rounded-2xl shadow-xl p-4 w-[380px] max-h-[90vh] overflow-y-auto z-10"
        >
          {/* Schließen-Button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 text-gray-400 hover:text-black text-xl"
          >
            ✕
          </button>

          {/* Stand-Bild */}
          {stand.photo && (
          <div className="mb-3 w-full overflow-hidden rounded bg-gray-100">
            {stand.photo ? (
              <img
                src={stand.photo}
                alt={t(stand.company)}
                className={`
                  w-full
                  object-contain
                  bg-white
                  rounded
                  transition-all
                  ${pdfMode
                    ? "h-[160px] max-w-[600px] mx-auto"
                    : "h-20 sm:h-28 md:h-32"
                  }
                `}
              />  ) : (
              <div className="
                w-full h-32 sm:h-40 md:h-48
                flex items-center justify-center
                text-gray-400 text-sm
                print:h-24
              ">
                {lang === "de" ? "Kein Bild verfügbar" : "No image available"}
              </div>
            )}
          </div>
          )}

          {/* Inhalt */}
          <div>
            {/* Firmenname */}
            <h2 className={`${pdfMode ? "text-2xl" : "text-xl"} font-bold mb-2 text-center`}>
              {t(stand.company)}
            </h2>

            {/* Standbezeichnung */}
            <p>
              <strong>{t(stand.label)}</strong>
            </p>

           {/*<p className="mt-2 text-gray-600 text-sm">
              <strong>
                {lang === "de" ? "Provinz:" : "Province:"}
              </strong>{" "}
              {t(stand.provinz)}
            </p> */}

            {/* Adresse */}
            <p className="mt-2 text-gray-600 text-sm">
            <br />
              <strong>{t(stand.companyLegal)}</strong>
              <br />
              {t(stand.street)} {t(stand.streetNumber)}
              <br />
              {t(stand.postalcode)} {t(stand.city)}
              <br />
              {t(stand.region)}
              <br />
              <br />
            </p>
            {/* Telefon (mobil anwählbar) */}
            <p>
              {lang === "de" ? "Telefon:" : "Phone:"}{" "}
              <a
                href={`tel:${t(stand.phone).replace(/\s+/g, "")}`}
                className="text-blue-600 font-semibold"
              >
                {t(stand.phone)}
              </a>
            </p>

            {/* E-Mail */}
            {stand.email && (
              <p className="mt-2 text-gray-600 text-sm">
                <strong>E-Mail:</strong>{" "}
                <a
                  href={`mailto:${stand.email}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {stand.email}
                </a>
              </p>
            )}

            {/* Website */}
            {stand.web && (
              <p className="mt-2 text-gray-600 text-sm">
                <strong>
                  {lang === "de" ? "Internet:" : "Website:"}
                </strong>{" "}
                <a
                  href={stand.web}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {stand.web}
                </a>
              </p>
            )}

            {/* Kontaktperson */}
            <p style={{ whiteSpace: "pre-line" }}>
              <strong>
                {lang === "de" ? "Kontakt:" : "Contact:"}{" "}
                {t(stand.contact)}
              </strong>
            </p>
            
            {/* Beschreibung */}
            <p className={`${pdfMode ? "text-base" : "text-sm"} text-gray-600 mt-2 text-justif`}>
              {t(stand.description)}
            </p>
          </div>

          {/* Meta-Information */}
          <div className="mt-4 border-t pt-2 text-xs text-gray-500">
            <p>
              <strong>ITA STAND:</strong> {stand.label}
            </p>
          </div>

          {/* Aktionsbuttons */}
          
          <div className="mt-4 flex justify-center gap-2">
            {!isKiosk && (
            <button
              onClick={handleShare}
              className="px-3 py-1 bg-blue-500 text-white rounded"
            >
              {lang === "de" ? "Teilen" : "Share"}
            </button>
            )}

            {!isKiosk && (
            <button
              onClick={handlePrint}
              className="px-3 py-1 bg-green-500 text-white rounded"
            >
              {lang === "de" ? "Speichern" : "Save"}
            </button>
          )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
